/**
 * Per-connector help content for the creation wizard (#233).
 *
 * Content is DERIVED FROM THE CONNECTOR SCHEMA the wizard already fetches, so it
 * stays keyed by connector type with no separate map to maintain:
 *   - "What you'll need" bullets  <- credential field labels (is_credential)
 *   - OAuth note                  <- presence of an oauth_button field
 *   - docs / setup links          <- documentation_url / setup_guide_url
 *
 * A tiny CONNECTOR_HELP_OVERRIDES layer adds curated *framing/notes* (never the
 * credential list -- that is always schema-derived) for connectors that benefit
 * from an extra operational reminder. Everything degrades to generic copy when
 * nothing is derivable.
 */
import type {
  ConnectorTypeSchemaResponse,
  FieldType,
} from '@/types/connector-schema';

export interface ConnectorHelp {
  /** "What you'll need" bullet list. */
  needs: string[];
  /** One-line OAuth reminder, or null when the connector has no OAuth field. */
  oauthNote: string | null;
  /** Optional curated operational note (from overrides). */
  note: string | null;
  /** External API documentation link, if the schema provides one. */
  docsUrl?: string;
  /** External setup-guide link, if the schema provides one. */
  setupUrl?: string;
  /** True when no connector-specific content could be derived (generic fallback). */
  isFallback: boolean;
}

/**
 * Field types that represent a credential the user actually types/uploads.
 * Button/OAuth/dynamic/hidden field types are excluded from the literal
 * "what you'll need" list (OAuth is summarised separately).
 */
const CREDENTIAL_FIELD_TYPES = new Set<FieldType>([
  'text',
  'password',
  'file',
  'textarea',
  'json',
  'number',
  'select',
]);

const GENERIC_NEED =
  'Provide the credentials and connection details for this source. Required fields are marked with *.';

/** Curated, schema-independent framing/notes. Keep small and stable. */
export const CONNECTOR_HELP_OVERRIDES: Record<
  string,
  { oauthNote?: string; note?: string }
> = {
  google_sheets: {
    note: 'Share the target spreadsheet with the connector’s service account before testing.',
  },
};

const titleCase = (s: string): string =>
  s
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const oauthProvider = (
  schema: ConnectorTypeSchemaResponse,
): string | undefined => {
  const field = schema.fields.find((f) => f.type === 'oauth_button');
  const provider = field?.oauth_config?.['provider'];
  return typeof provider === 'string' && provider ? titleCase(provider) : undefined;
};

/** Documentation links, sourced from the connector-types list/detail endpoint. */
export interface ConnectorHelpLinks {
  documentation_url?: string;
  setup_guide_url?: string;
}

/**
 * Derive the help content for a connector from its schema (+ overrides).
 *
 * Docs links come from `links` (the connector-types list/detail payload) because
 * the per-type /schema endpoint does NOT return documentation_url; the schema is
 * still consulted as a fallback in case that ever changes.
 *
 * Returns generic fallback copy when `schema` is missing or yields nothing.
 */
export const deriveConnectorHelp = (
  schema: ConnectorTypeSchemaResponse | undefined,
  connectorType: string,
  links: ConnectorHelpLinks = {},
): ConnectorHelp => {
  const override = CONNECTOR_HELP_OVERRIDES[connectorType] ?? {};
  const docsUrl = links.documentation_url || schema?.documentation_url || undefined;
  const setupUrl = links.setup_guide_url || schema?.setup_guide_url || undefined;

  if (!schema) {
    const hasLinks = !!docsUrl || !!setupUrl;
    return {
      needs: [GENERIC_NEED],
      oauthNote: null,
      note: override.note ?? null,
      docsUrl,
      setupUrl,
      isFallback: !hasLinks,
    };
  }

  const needs = schema.fields
    .filter((f) => f.is_credential && CREDENTIAL_FIELD_TYPES.has(f.type))
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((f) => f.label)
    .filter((label): label is string => !!label);

  const hasOAuth = schema.fields.some((f) => f.type === 'oauth_button');
  let oauthNote: string | null = null;
  if (override.oauthNote) {
    oauthNote = override.oauthNote;
  } else if (hasOAuth) {
    const provider = oauthProvider(schema);
    oauthNote = `Sign in${
      provider ? ` with ${provider}` : ''
    } on this step to authorize access — there’s no client secret to paste.`;
  }

  const hasContent =
    needs.length > 0 || !!oauthNote || !!docsUrl || !!setupUrl;

  return {
    needs: hasContent ? needs : [GENERIC_NEED],
    oauthNote,
    note: override.note ?? null,
    docsUrl,
    setupUrl,
    isFallback: !hasContent,
  };
};
